import {
  DropdownAlignment,
  DropdownPosition,
  IDropdownProps,
  RectPos,
} from "./Dropdown.interface";

export interface CalculateDropdownPosProps
  extends Pick<IDropdownProps, "position" | "alignment"> {
  btnRect: DOMRect;
  listSizes: { width: number; height: number };
  reposition: boolean;
}

// eslint-disable-next-line complexity
export const calculateDropdownPos = ({
  position,
  alignment,
  btnRect,
  listSizes,
  reposition,
}: CalculateDropdownPosProps): {
  newPos: RectPos;
  currentPosition: DropdownPosition;
  currentAlignment: DropdownAlignment;
} => {
  const newPos: RectPos = {};

  let currentPosition = position;
  let currentAlignment = alignment;

  if (reposition) {
    if ([DropdownPosition.LEFT, DropdownPosition.RIGHT].includes(position)) {
      // ~ 1px for ceil
      const overflowedLeft = btnRect.left - listSizes.width <= 1;
      const overflowedRight =
        btnRect.left + btnRect.width + listSizes.width >= window.innerWidth - 1;

      if (overflowedLeft && overflowedRight) {
        currentPosition = DropdownPosition.BOTTOM;
        currentAlignment = DropdownAlignment.MIDDLE;
      } else {
        switch (position) {
          case DropdownPosition.RIGHT:
            if (overflowedRight) {
              currentPosition = DropdownPosition.LEFT;
            }
            break;
          case DropdownPosition.LEFT:
            if (overflowedLeft) {
              currentPosition = DropdownPosition.RIGHT;
            }
            break;
          default:
        }
      }
    }

    if ([DropdownPosition.BOTTOM, DropdownPosition.TOP].includes(position)) {
      const overflowedTop = btnRect.top <= listSizes.height;
      const overflowedBottom =
        btnRect.top + btnRect.height + listSizes.height >= window.innerHeight;

      const overflowedLeft = btnRect.right - listSizes.width < 0;
      const overflowedRight =
        btnRect.left + listSizes.width > window.innerWidth;

      if (overflowedLeft && overflowedRight) {
        currentAlignment = DropdownAlignment.STRETCH;
      } else {
        switch (currentAlignment) {
          case DropdownAlignment.START:
            if (overflowedRight) {
              currentAlignment = DropdownAlignment.END;
            }
            break;
          case DropdownAlignment.END:
            if (overflowedLeft) {
              currentAlignment = DropdownAlignment.START;
            }
            break;
          case DropdownAlignment.MIDDLE:
            if (overflowedLeft) {
              currentAlignment = DropdownAlignment.START;
            }
            if (overflowedRight) {
              currentAlignment = DropdownAlignment.END;
            }
            break;
          default:
        }
      }

      if (overflowedTop && overflowedBottom) {
        // both top and bottom
        currentPosition = DropdownPosition.BOTTOM;
        currentAlignment = DropdownAlignment.STRETCH;
      } else {
        switch (position) {
          case DropdownPosition.TOP:
            if (overflowedTop) {
              // only top
              currentPosition = DropdownPosition.BOTTOM;
            }
            break;
          case DropdownPosition.BOTTOM:
            if (overflowedBottom) {
              // only bottom
              currentPosition = DropdownPosition.TOP;
            }
            break;
          default:
        }
      }
    }
  }

  switch (currentPosition) {
    case DropdownPosition.LEFT:
      newPos.left = `${btnRect.left - listSizes.width + window.scrollX}px`;
      break;
    case DropdownPosition.RIGHT:
      newPos.left = `${btnRect.left + btnRect.width + window.scrollX}px`;
      break;
    case DropdownPosition.TOP:
      newPos.top = `${btnRect.top - listSizes.height + window.scrollY}px`;
      break;
    case DropdownPosition.BOTTOM:
    default:
      newPos.top = `${btnRect.bottom + window.scrollY}px`;
      break;
  }
  switch (currentAlignment) {
    case DropdownAlignment.FIT:
      if (
        [DropdownPosition.TOP, DropdownPosition.BOTTOM].includes(
          currentPosition,
        )
      ) {
        newPos.left = `${btnRect.left + window.scrollX}px`;
        newPos.width = `${btnRect.width}px`;
      } else {
        newPos.top = `${btnRect.top + window.scrollY}px`;
        newPos.height = `${btnRect.height}px`;
      }
      break;
    case DropdownAlignment.END:
      if (
        [DropdownPosition.TOP, DropdownPosition.BOTTOM].includes(
          currentPosition,
        )
      ) {
        newPos.left = `${
          btnRect.left + btnRect.width - listSizes.width + window.scrollX
        }px`;
      } else {
        //
        newPos.top = `${
          btnRect.top + btnRect.height - listSizes.height + window.scrollY
        }px`;
      }
      break;
    case DropdownAlignment.MIDDLE:
      if (
        [DropdownPosition.TOP, DropdownPosition.BOTTOM].includes(
          currentPosition,
        )
      ) {
        newPos.left = `${
          btnRect.left +
          btnRect.width / 2 -
          listSizes.width / 2 +
          window.scrollX
        }px`;
      } else {
        newPos.top = `${
          btnRect.top +
          btnRect.height / 2 -
          listSizes.height / 2 +
          window.scrollY
        }px`;
      }
      break;
    case DropdownAlignment.STRETCH:
      if (
        [DropdownPosition.TOP, DropdownPosition.BOTTOM].includes(
          currentPosition,
        )
      ) {
        const scrollBarWidth =
          globalThis.innerWidth -
          globalThis.document.documentElement.clientWidth;
        newPos.left = "0";
        newPos.width = `calc(100vw - ${scrollBarWidth}px)`;
      } else {
        newPos.top = "0";
        newPos.height = `${
          document.documentElement.getBoundingClientRect().height
        }px`; // full height of document
      }
      break;
    case DropdownAlignment.START:
    default:
      if (
        [DropdownPosition.TOP, DropdownPosition.BOTTOM].includes(
          currentPosition,
        )
      ) {
        newPos.left = `${btnRect.left + window.scrollX}px`;
      } else {
        newPos.top = `${btnRect.top + window.scrollY}px`;
      }
      break;
  }

  return { newPos, currentAlignment, currentPosition };
};
